import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Space,
  Modal,
  Button,
  Form,
  Input,
  Row,
  Col,
  message,
  Tabs,
  InputNumber,
  Popconfirm,
  Select,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, CheckOutlined, CloseOutlined, PrinterOutlined } from '@ant-design/icons';
import printQaQcCertification from './qaqc-certification-print';
import { QaqcFiberPrint } from './qaqc-fiber-print';
import { QaqcLabPrint } from './qaqc-lab-print';
import { QaqcAnalysisService } from '@gtpl/shared-services/raw-material-procurement';
import { UpdateQaqcFibreAnalysisReq, UpdateQaqcFibreLabAnalysisReq, QaqcApprovalReq } from '@gtpl/shared-services/raw-material-procurement';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { Link } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import moment from 'moment';
import { Excel } from 'antd-table-saveas-excel';
import { ProductService, SkuService } from '@gtpl/shared-services/masters';
import { QualityTypeEnum } from '@gtpl/shared-models/common-models';
const { RangePicker } = DatePicker;


type AnalysisType = 'fiber' | 'lab' | null;

const QaqcDetailsView: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [analysisType, setAnalysisType] = useState<AnalysisType>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('work-order');
  const [fibreAnalysisData, setFibreAnalysisData] = useState<any[]>([]);
  const [fibreLabAnalysisData, setFibreLabAnalysisData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const productService = new ProductService();
  const [productsData, setProductsData] = useState<any[]>([]);
  const [skuCodesData, setSkuCodesData] = useState<any[]>([]);
  const [lotNumDropdownData, setLotNumDropdownData] = useState<any[]>([]);
  const [workOrdersDropdownData, setWorkOrdersDropdownData] = useState<any[]>([]);
  const { Option } = Select;
  const qaqcService = new QaqcAnalysisService();
  const skucodeservice = new SkuService();
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState<any>(null);
  const [printType, setPrintType] = useState<'fiber' | 'lab' | null>(null);
  const [page, setPage] = React.useState(1); 
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    getAllProducts();
    getAllSkuCodes();
    getLotsDropdownForQAQC()
    getWorkOrdersDropdownForQAQC();
  }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const qaqcService = new QaqcAnalysisService();

      const req: any = {};
      if (filterForm.getFieldValue('variant')) {
        req.variant = filterForm.getFieldValue('variant')
      }
      if (filterForm.getFieldValue('quality')) {
        req.quality = filterForm.getFieldValue('quality')
      }
      if (filterForm.getFieldValue('testDate') && filterForm.getFieldValue('testDate')[0]) {
        req.fromDate = (filterForm.getFieldValue('testDate')[0]).format('YYYY-MM-DD');
        req.toDate = (filterForm.getFieldValue('testDate')[1]).format('YYYY-MM-DD');
      }
      if (activeTab === 'work-order') {
        if (filterForm.getFieldValue('WorkOrder')) {
          req.workOrderNo = filterForm.getFieldValue('WorkOrder')
        }
        // Fetch all fibre lab analysis data
        const labResponse = await qaqcService.getAllFibreLabAnalysis(req);
        if (labResponse.status) {
          const labData = labResponse.data.filter((item: any) => !item.lotNumber).map((item: any) => ({
            ...item,
            quantity: item.quantity ?? Number(item.physicalQuantity) ?? 0,
          }));
          setFibreLabAnalysisData(labData);
        } else {
          AlertMessages.getErrorMessage(labResponse.internalMessage);
          setFibreLabAnalysisData([]);
        }
      } else {
        if (filterForm.getFieldValue('lotNo')) {
          req.lotNumber = filterForm.getFieldValue('lotNo')
        }
        // Fetch all fibre analysis data
        const fibreResponse = await qaqcService.getAllFibreAnalysis(req);
        if (fibreResponse.status) {
          const fibreData = fibreResponse.data.filter((item: any) => item.lotNumber);
          setFibreAnalysisData(fibreData);
        } else {
          AlertMessages.getErrorMessage(fibreResponse.internalMessage);
          setFibreAnalysisData([]);
        }
      }
    } catch (error) {
      AlertMessages.getErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (type: AnalysisType, record: any) => {
    setAnalysisType(type);
    setSelectedRecord(record);
    setIsModalVisible(true);

    // Populate form with existing data
    if (type === 'fiber') {
      form.setFieldsValue({
        meanLength: Number(record.meanLength),
        shortestFiber: Number(record.shortestFiber),
        longestFiber: Number(record.longestFiber),
        shortFiberPercent: Number(record.shortFiberPercent),
        slub: Number(record.slub),
        remarks: record.remarks,
      });
    } else {
      form.setFieldsValue({
        oilTest: Number(record.oilTest),
        oilTestRemarks: record.oilTestRemarks,
        sericinTest: Number(record.sericinTest),
        sericinTestRemarks: record.sericinTestRemarks,
        acidityTest: Number(record.acidityTest),
        acidityTestRemarks: record.acidityTestRemarks,
        ppContent: Number(record.ppContent),
        ppContentRemarks: record.ppContentRemarks,
        moistureContent: Number(record.moistureContent),
        moistureContentRemarks: record.moistureContentRemarks,
        remarks: record.remarks,
      });
    }
  };

  const getAllProducts = () => {
    productService.getAllActiveProducts().then(res => {
      if (res.status) {
        console.log(res.data)
        setProductsData(res.data);
      } else {
        if (res.intlCode) {
          setProductsData([]);
          AlertMessages.getErrorMessage('No Data Found');
        } else {
          AlertMessages.getErrorMessage(res.internalMessage);
        }
      }
    }).catch(err => {
      AlertMessages.getErrorMessage(err.message);
      setProductsData([]);
    })
  }

  const getAllSkuCodes = () => {
    skucodeservice.getActiveSkuCodes().then(res => {
      if (res.status) {
        setSkuCodesData(res.data);
      } else {
        if (res.intlCode) {
          setSkuCodesData([]);
          AlertMessages.getErrorMessage('No Data Found');
        } else {
          AlertMessages.getErrorMessage(res.internalMessage);
        }
      }
    }).catch(err => {
      AlertMessages.getErrorMessage(err.message);
      setSkuCodesData([]);
    })
  }

  const getProductOptionLabel = (product: any) =>
    product.productName || product.name || product.shortCode || product.product || '';

  const getProductOptionValue = (product: any) =>
    product.productId ?? product.product_id ?? product.id;

  const getSkuCodeOptionLabel = (sku: any) =>
    sku.shortCode || sku.short_code || sku.skuCode || sku.sku_code || '';

  const getSkuCodeOptionValue = (sku: any) =>
    sku.skuCodeId ?? sku.sku_code_id ?? sku.id;

  const handleTabChange = (key: string) => {
    filterForm.resetFields(['variant', 'WorkOrder', 'lotNo']);
    setPage(1);
    setActiveTab(key);
  };

  const getWorkOrdersDropdownForQAQC = () => {
    qaqcService.getWorkOrdersDropdownForQAQC().then(res => {
      if (res.status) {
        console.log(res.data)
        setWorkOrdersDropdownData(res.data);
      } else {
        if (res.intlCode) {
          setWorkOrdersDropdownData([]);
          AlertMessages.getErrorMessage('No Data Found');
        } else {
          AlertMessages.getErrorMessage(res.internalMessage);
        }
      }
    }).catch(err => {
      AlertMessages.getErrorMessage(err.message);
      setWorkOrdersDropdownData([]);
    })
  }

  const getLotsDropdownForQAQC = () => {
    qaqcService.getLotsDropdownForQAQC().then(res => {
      if (res.status) {
        console.log(res.data)
        setLotNumDropdownData(res.data);
      } else {
        if (res.intlCode) {
          setLotNumDropdownData([]);
          AlertMessages.getErrorMessage('No Data Found');
        } else {
          AlertMessages.getErrorMessage(res.internalMessage);
        }
      }
    }).catch(err => {
      AlertMessages.getErrorMessage(err.message);
      setLotNumDropdownData([]);
    })
  }
  const closeModal = () => {
    setIsModalVisible(false);
    setAnalysisType(null);
    setSelectedRecord(null);
    form.resetFields();
  };

  function handleSearch(selectedKeys, confirm, dataIndex) {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  function handleReset(clearFilters) {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: visible => {
      if (visible) { setTimeout(() => searchInput.current.select()); }
    },
    render: text =>
      text ? (
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        ) : text
      ) : null
  });
  const onUpdate = async (values: any) => {
    try {
      const qaqcService = new QaqcAnalysisService();

      // If lotNumber exists, workOrderNo should not be null, use empty string or actual value
      const workOrderNo = selectedRecord.lotNumber
        ? (selectedRecord.workOrderNo || '')
        : selectedRecord.workOrderNo || '';

      const basePayload = {
        id: selectedRecord.id,
        workOrderNo: workOrderNo,
        lotNumber: selectedRecord.lotNumber || '',
        operation: selectedRecord.operation || '',
        prevOperation: selectedRecord.prevOperation || '',
        product: selectedRecord.product || selectedRecord.variant || '',
        quality: selectedRecord.quality || null,
        quantity: Number(selectedRecord.quantity ??
          selectedRecord.physicalQuantity ??
          0), testType: selectedRecord.lotNumber ? 'lot' : 'work_order',
        analysisType: analysisType === 'fiber' ? 'qaqcfibreanalysis' : 'qaqcfibrelabanalysis',
        remarks: selectedRecord.remarks || '',
        createdUser: 'admin',
        updatedUser: 'admin',
        createdAt: selectedRecord.createdAt,
        isApproved: selectedRecord.isApproved || false,
      };

      let result;
      if (analysisType === 'fiber') {
        const fiberPayload = {
          ...basePayload,
          meanLength: Number(values.meanLength),
          shortestFiber: Number(values.shortestFiber),
          longestFiber: Number(values.longestFiber),
          shortFiberPercent: Number(values.shortFiberPercent),
          slub: Number(values.slub),
          remarks: values.remarks,
        };
        result = await qaqcService.updateFibreAnalysis(fiberPayload);
      } else {
        const labPayload = {
          ...basePayload,
          oilTest: Number(values.oilTest),
          oilTestRemarks: values.oilTestRemarks,
          sericinTest: Number(values.sericinTest),
          sericinTestRemarks: values.sericinTestRemarks,
          acidityTest: Number(values.acidityTest),
          acidityTestRemarks: values.acidityTestRemarks,
          ppContent: Number(values.ppContent),
          ppContentRemarks: values.ppContentRemarks,
          moistureContent: Number(values.moistureContent),
          moistureContentRemarks: values.moistureContentRemarks,
          remarks: values.remarks,
        };

        console.log('Base Paaayload:', basePayload);
        console.log('Lab Payload:', labPayload);
        result = await qaqcService.updateFibreLabAnalysis(labPayload);
      }

      if (result.status) {
        AlertMessages.getSuccessMessage('Analysis updated successfully');
        closeModal();
        fetchData(); // Refresh data
      } else {
        AlertMessages.getErrorMessage(result.internalMessage);
      }
    } catch (error) {
      AlertMessages.getErrorMessage(error.message);
    }
  };
  const onReset = () => {
    filterForm.resetFields();
    fetchData();
  }

  const buildBaseQaqcFilterReq = (includeVariant = true) => {
    const req: any = {};
    if (includeVariant && filterForm.getFieldValue('variant')) {
      req.variant = filterForm.getFieldValue('variant');
    }
    if (filterForm.getFieldValue('quality')) {
      req.quality = filterForm.getFieldValue('quality');
    }
    const testDate = filterForm.getFieldValue('testDate');
    if (testDate && testDate[0]) {
      req.fromDate = testDate[0].format('YYYY-MM-DD');
      req.toDate = testDate[1].format('YYYY-MM-DD');
    }
    return req;
  };

const formatTestDateCell = (text: any) =>
  text || '-';

  const exportExcel = async () => {
  try {
    message.loading({ content: 'Preparing Excel…', key: 'qaqc-details-excel', duration: 0 });

    const labReq = { ...buildBaseQaqcFilterReq(activeTab === 'work-order') };

    if (filterForm.getFieldValue('WorkOrder')) {
      labReq.workOrderNo = filterForm.getFieldValue('WorkOrder');
    }

    const fibreReq = { ...buildBaseQaqcFilterReq(activeTab === 'lot') };

    if (filterForm.getFieldValue('lotNo')) {
      fibreReq.lotNumber = filterForm.getFieldValue('lotNo');
    }

    const [labResponse, fibreResponse] = await Promise.all([
      qaqcService.getAllFibreLabAnalysis(labReq),
      qaqcService.getAllFibreAnalysis(fibreReq),
    ]);

    let labRows: any[] = [];

    if (labResponse.status) {
      labRows = labResponse.data
        .filter((item: any) => !item.lotNumber)
        .map((item: any) => ({
          ...item,
          quantity:
            item.quantity !== null &&
            item.quantity !== undefined &&
            item.quantity !== ''
              ? item.quantity
              : item.physicalQuantity !== null &&
                item.physicalQuantity !== undefined &&
                item.physicalQuantity !== ''
              ? Number(item.physicalQuantity)
              : '-',
        }));
    } else {
      AlertMessages.getErrorMessage(labResponse.internalMessage);
    }

    let fibreRows: any[] = [];

    if (fibreResponse.status) {
      fibreRows = fibreResponse.data.filter((item: any) => item.lotNumber);
    } else {
      AlertMessages.getErrorMessage(fibreResponse.internalMessage);
    }

    // Columns match first tab (Silk Fiber Lab Analysis) when activeTab === 'work-order' — no Lot / Operation / Prev Operation
    const labColumns = [
      { title: 'S No', dataIndex: 'sno' },
      { title: 'Work Order No', dataIndex: 'workOrderNo' },
      { title: 'Test Date', dataIndex: 'testDate' },
      { title: 'Variant', dataIndex: 'product' },
      { title: 'Quality', dataIndex: 'quality' },
      { title: 'Oil Test', dataIndex: 'oilTest' },
      { title: 'Oil Remarks', dataIndex: 'oilTestRemarks' },
      { title: 'Sericin Test', dataIndex: 'sericinTest' },
      { title: 'Sericin Remarks', dataIndex: 'sericinTestRemarks' },
      { title: 'Acidity Test', dataIndex: 'acidityTest' },
      { title: 'Acidity Remarks', dataIndex: 'acidityTestRemarks' },
      { title: 'PP Content', dataIndex: 'ppContent' },
      { title: 'PP Remarks', dataIndex: 'ppContentRemarks' },
      { title: 'Moisture Content', dataIndex: 'moistureContent' },
      { title: 'Moisture Remarks', dataIndex: 'moistureContentRemarks' },
      { title: 'Quantity', dataIndex: 'quantity' },
    ];

    const labExportData = labRows.map((row, index) => ({
      sno: index + 1,
      workOrderNo: row.workOrderNo ?? '-',
      testDate: row.testDate ? formatTestDateCell(row.testDate) : '-',
      product: row.product ?? '-',
      quality: row.quality ?? '-',

      oilTest:
        row.oilTest !== null &&
        row.oilTest !== undefined &&
        row.oilTest !== ''
          ? row.oilTest
          : '-',

      oilTestRemarks: row.oilTestRemarks || '-',

      sericinTest:
        row.sericinTest !== null &&
        row.sericinTest !== undefined &&
        row.sericinTest !== ''
          ? row.sericinTest
          : '-',

      sericinTestRemarks: row.sericinTestRemarks || '-',

      acidityTest:
        row.acidityTest !== null &&
        row.acidityTest !== undefined &&
        row.acidityTest !== ''
          ? row.acidityTest
          : '-',

      acidityTestRemarks: row.acidityTestRemarks || '-',

      ppContent:
        row.ppContent !== null &&
        row.ppContent !== undefined &&
        row.ppContent !== ''
          ? row.ppContent
          : '-',

      ppContentRemarks: row.ppContentRemarks || '-',

      moistureContent:
        row.moistureContent !== null &&
        row.moistureContent !== undefined &&
        row.moistureContent !== ''
          ? row.moistureContent
          : '-',

      moistureContentRemarks: row.moistureContentRemarks || '-',

      quantity:
        row.quantity !== null &&
        row.quantity !== undefined &&
        row.quantity !== ''
          ? row.quantity
          : '-',
    }));

    // Columns match second tab (Silk Fiber Analysis) when activeTab === 'lot' — no Work Order No
    const fibreColumns = [
      { title: 'S No', dataIndex: 'sno' },
      { title: 'Lot Number', dataIndex: 'lotNumber' },
      { title: 'Test Date', dataIndex: 'testDate' },
      { title: 'Operation', dataIndex: 'operation' },
      { title: 'Prev Operation', dataIndex: 'prevOperation' },
      { title: 'Variant', dataIndex: 'product' },
      { title: 'Quality', dataIndex: 'quality' },
      { title: 'Mean Length', dataIndex: 'meanLength' },
      { title: 'Shortest Fiber', dataIndex: 'shortestFiber' },
      { title: 'Longest Fiber', dataIndex: 'longestFiber' },
      { title: 'Short Fiber %', dataIndex: 'shortFiberPercent' },
      { title: 'Slub', dataIndex: 'slub' },
      { title: 'Remarks', dataIndex: 'remarks' },
      { title: 'Quantity', dataIndex: 'quantity' },
    ];

    const qty = (row: any) =>
      row.quantity !== null &&
      row.quantity !== undefined &&
      row.quantity !== ''
        ? row.quantity
        : row.physicalQuantity !== null &&
          row.physicalQuantity !== undefined &&
          row.physicalQuantity !== ''
        ? Number(row.physicalQuantity)
        : '-';

    const fibreExportData = fibreRows.map((row, index) => ({
      sno: index + 1,
      lotNumber: row.lotNumber ? row.lotNumber : '-',
      testDate: row.testDate ? formatTestDateCell(row.testDate) : '-',
      operation: row.operation ?? '-',
      prevOperation: row.prevOperation ?? '-',
      product: row.product ?? '-',
      quality: row.quality ?? '-',

      meanLength:
        row.meanLength !== null &&
        row.meanLength !== undefined &&
        row.meanLength !== ''
          ? row.meanLength
          : '-',

      shortestFiber:
        row.shortestFiber !== null &&
        row.shortestFiber !== undefined &&
        row.shortestFiber !== ''
          ? row.shortestFiber
          : '-',

      longestFiber:
        row.longestFiber !== null &&
        row.longestFiber !== undefined &&
        row.longestFiber !== ''
          ? row.longestFiber
          : '-',

      shortFiberPercent:
        row.shortFiberPercent !== null &&
        row.shortFiberPercent !== undefined &&
        row.shortFiberPercent !== ''
          ? row.shortFiberPercent
          : '-',

      slub:
        row.slub !== null &&
        row.slub !== undefined &&
        row.slub !== ''
          ? row.slub
          : '-',

      remarks: row.remarks ?? '-',

      quantity: qty(row),
    }));

    const excel = new Excel();

    const labSheet = excel.addSheet('Silk Fiber Lab Analysis');
    labSheet.addColumns(labColumns);
    labSheet.addDataSource(labExportData);

    const fibreSheet = excel.addSheet('Silk Fiber Analysis');
    fibreSheet.addColumns(fibreColumns);
    fibreSheet.addDataSource(fibreExportData);

    excel.saveAs('QAQC-Details.xlsx');

    message.success({
      content: 'Excel downloaded',
      key: 'qaqc-details-excel',
      duration: 2,
    });
  } catch (error: any) {
    message.error({
      content: error?.message || 'Export failed',
      key: 'qaqc-details-excel',
      duration: 3,
    });

    AlertMessages.getErrorMessage(error?.message);
  }
};

  const onApprove = async (record: any) => {
    try {
      const qaqcService = new QaqcAnalysisService();
      const payload = {
        id: record.id,
        testType: record.lotNumber ? 'lot' : 'work_order',
        analysisType: record.analysisType,
        workOrderNo: record.workOrderNo,
        lotNumber: record.lotNumber,
        productId: record.productId
      };

      const result = await qaqcService.approveQaqcTest(payload);

      if (result.status) {
        AlertMessages.getSuccessMessage('QAQC test approved successfully');
        fetchData(); // Refresh data
      } else {
        AlertMessages.getErrorMessage(result.internalMessage);
      }
    } catch (error) {
      AlertMessages.getErrorMessage(error.message);
    }
  };

  const openPrintModal = (record: any, type: 'fiber' | 'lab') => {
    setPrintRecord(record);
    setPrintType(type);
    setPrintModalVisible(true);
  };

  const closePrintModal = () => {
    setPrintModalVisible(false);
    setPrintRecord(null);
    setPrintType(null);
  };

  const getFibreAnalysisColumns = (): ColumnsType<any> => {
    const baseColumns: ColumnsType<any> = [
      {
          title: "S No",
          key: "id",
          responsive: ["md"],
          align: "left",
          render: (text, object, index) => (page - 1) * 10 + (index + 1),
        },
      {
        title: 'Work Order No',
        dataIndex: 'workOrderNo',
        sorter: (a, b) => (a.workOrderNo || '').localeCompare(b.workOrderNo || ''),
        ...getColumnSearchProps('workOrderNo'),
      },

      {
        title: 'Lot Number',
        dataIndex: 'lotNumber',
        render: (lot) => lot || 'N/A',
        sorter: (a, b) => (a.lotNumber || '').localeCompare(b.lotNumber || ''),
        ...getColumnSearchProps('lotNumber'),
      },

      {
  title: 'Test Date',
  dataIndex: 'testDate',
 sorter: (a, b) => a.testDate?.localeCompare(b.testDate),
  ...getColumnSearchProps('testDate'),
},

      {
        title: 'Operation',
        dataIndex: 'operation',
        sorter: (a, b) => (a.operation || '').localeCompare(b.operation || ''),
        ...getColumnSearchProps('operation'),
      },

      {
        title: 'Prev Operation',
        dataIndex: 'prevOperation',
        sorter: (a, b) => (a.prevOperation || '').localeCompare(b.prevOperation || ''),
        ...getColumnSearchProps('prevOperation'),
      },

      {
        title: 'Variant',
        dataIndex: 'product',
        sorter: (a, b) => (a.product || '').localeCompare(b.product || ''),
        ...getColumnSearchProps('product'),
      },

      {
        title: 'Quality',
        dataIndex: 'quality',
        sorter: (a, b) => (a.quality || '').localeCompare(b.quality || ''),
        ...getColumnSearchProps('quality'),
      },

      {
        title: 'Mean Length',
        dataIndex: 'meanLength',
        // sorter: (a, b) => (a.meanLength || 0) - (b.meanLength || 0),
        // ...getColumnSearchProps('meanLength'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Shortest Fiber',
        dataIndex: 'shortestFiber',
        // sorter: (a, b) => (a.shortestFiber || 0) - (b.shortestFiber || 0),
        // ...getColumnSearchProps('shortestFiber'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Longest Fiber',
        dataIndex: 'longestFiber',
        // sorter: (a, b) => (a.longestFiber || 0) - (b.longestFiber || 0),
        // ...getColumnSearchProps('longestFiber'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Short Fiber %',
        dataIndex: 'shortFiberPercent',
        // sorter: (a, b) => (a.shortFiberPercent || 0) - (b.shortFiberPercent || 0),
        // ...getColumnSearchProps('shortFiberPercent'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Slub',
        dataIndex: 'slub',
        // sorter: (a, b) => (a.slub || 0) - (b.slub || 0),
        // ...getColumnSearchProps('slub'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Remarks',
        dataIndex: 'remarks',
        // sorter: (a, b) => (a.remarks || '').localeCompare(b.remarks || ''),
        // ...getColumnSearchProps('remarks'),
        render: (value) => value || '-',
      },

      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
        ...getColumnSearchProps('quantity'),
        render: (value) => Number(Number(value).toFixed(2)),
      },

      {
        title: 'Actions',
        render: (_, record) => (
          <Space>
            {!record.isQAQCApproved ? (
              <>
                <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal('fiber', record)} />
                <Popconfirm
                  title="Are you sure you want to approve this QAQC test?"
                  onConfirm={() => onApprove(record)}
                >
                  <Button type="link" icon={<CheckOutlined />} />
                </Popconfirm>
              </>
            ) : (
              <Button type="link" icon={<PrinterOutlined />} onClick={() => openPrintModal(record, 'fiber')} />
            )}
          </Space>
        ),
      },
    ];

    if (activeTab === 'work-order') {
      return baseColumns.filter(
        col => !('dataIndex' in col) || !['lotNumber', 'operation', 'prevOperation'].includes(col.dataIndex as string)
      );
    }

    if (activeTab === 'lot') {
      return baseColumns.filter(
        col => !('dataIndex' in col) || col.dataIndex !== 'workOrderNo'
      );
    }

    return baseColumns;
  };

  const getFibreLabAnalysisColumns = (): ColumnsType<any> => {
    const baseColumns: ColumnsType<any> = [
      {
          title: "S No",
          key: "id",
          responsive: ["md"],
          align: "left",
          render: (text, object, index) => (page - 1) * 10 + (index + 1),
        },
      {
        title: 'Work Order No',
        dataIndex: 'workOrderNo',
        sorter: (a, b) => (a.workOrderNo || '').localeCompare(b.workOrderNo || ''),
        ...getColumnSearchProps('workOrderNo'),
      },

      {
        title: 'Lot Number',
        dataIndex: 'lotNumber',
        render: (lot) => lot || '-',
        sorter: (a, b) => (a.lotNumber || '').localeCompare(b.lotNumber || ''),
        ...getColumnSearchProps('lotNumber'),
      },

      {
  title: 'Test Date',
  dataIndex: 'testDate',
 sorter: (a, b) => a.testDate?.localeCompare(b.testDate),
  ...getColumnSearchProps('testDate'),
},

      {
        title: 'Operation',
        dataIndex: 'operation',
        sorter: (a, b) => (a.operation || '').localeCompare(b.operation || ''),
        ...getColumnSearchProps('operation'),
      },

      {
        title: 'Prev Operation',
        dataIndex: 'prevOperation',
        sorter: (a, b) => (a.prevOperation || '').localeCompare(b.prevOperation || ''),
        ...getColumnSearchProps('prevOperation'),
      },

      {
        title: 'Variant',
        dataIndex: 'product',
        sorter: (a, b) => (a.product || '').localeCompare(b.product || ''),
        ...getColumnSearchProps('product'),
      },

      {
        title: 'Quality',
        dataIndex: 'quality',
        sorter: (a, b) => (a.quality || '').localeCompare(b.quality || ''),
        ...getColumnSearchProps('quality'),
      },

      {
        title: 'Oil Test',
        dataIndex: 'oilTest',
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },
      {
        title: 'Oil Remarks',
        dataIndex: 'oilTestRemarks',
        render: (val) => val || '-',
      },

      {
        title: 'Sericin Test',
        dataIndex: 'sericinTest',
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },
      {
        title: 'Sericin Remarks',
        dataIndex: 'sericinTestRemarks',
        render: (val) => val || '-',
      },

      {
        title: 'Acidity Test',
        dataIndex: 'acidityTest',
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },
      {
        title: 'Acidity Remarks',
        dataIndex: 'acidityTestRemarks',
        render: (val) => val || '-',
      },

      {
        title: 'PP Content',
        dataIndex: 'ppContent',
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },
      {
        title: 'PP Remarks',
        dataIndex: 'ppContentRemarks',
        render: (val) => val || '-',
      },

      {
        title: 'Moisture Content',
        dataIndex: 'moistureContent',
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },
      {
        title: 'Moisture Remarks',
        dataIndex: 'moistureContentRemarks',
        render: (val) => val || '-',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
        ...getColumnSearchProps('quantity'),
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
      },

      {
        title: 'Actions',
        render: (_, record) => (
          <Space>
            {!record.isQAQCApproved ? (
              <>
                <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal('lab', record)} />
                <Popconfirm
                  title="Are you sure you want to approve this QAQC test?"
                  onConfirm={() => onApprove(record)}
                >
                  <Button type="link" icon={<CheckOutlined />} />
                </Popconfirm>
              </>
            ) : (
              <Button type="link" icon={<PrinterOutlined />} onClick={() => openPrintModal(record, 'lab')} />
            )}
          </Space>
        ),
      },
    ];

    if (activeTab === 'work-order') {
      return baseColumns.filter(
        col => !('dataIndex' in col) || !['lotNumber', 'operation', 'prevOperation'].includes(col.dataIndex as string)
      );
    }

    if (activeTab === 'lot') {
      return baseColumns.filter(
        col => !('dataIndex' in col) || col.dataIndex !== 'workOrderNo'
      );
    }

    return baseColumns;
  };

  return (
    <Card
      title={<span style={{ color: 'white' }}>QAQC Details</span>}
      style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}
      extra={
        <>
        <Button type='primary' style={{marginRight:'8px'}}onClick={exportExcel}>Get Excel</Button>
        <Link to="/antibiotics-view">
          <span style={{ color: 'white' }}>
            <Button type='primary'>Create</Button>
          </span>
        </Link>
        </>
        
      }
    >
      <Form form={filterForm} onFinish={fetchData} layout="inline" style={{ marginBottom: 16 }}>
        <Row gutter={16} style={{ width: '100%' }}>
          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item
              name="testDate"
              label="Test Date"
            >
              <DatePicker.RangePicker />
            </Form.Item>
          </Col>
          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item
              name="variant"
              label="Variant"
            >
              <Select
                placeholder="Select Variant"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {activeTab === 'work-order'
                  ? productsData.map(product => {
                    const value = getProductOptionValue(product);
                    const label = getProductOptionLabel(product);
                    return (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    );
                  })
                  : skuCodesData.map(sku => {
                    const value = getSkuCodeOptionValue(sku);
                    const label = getSkuCodeOptionLabel(sku);
                    return (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
          {activeTab === 'work-order' && (
            <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item
                name="WorkOrder"
                label="Work Order"
              >
                <Select
                  placeholder="Select Work Order"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {workOrdersDropdownData.map(workOrder => {
                    return <Option value={workOrder.workOrderNo}>{workOrder.workOrderNo}</Option>
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}
          {activeTab === 'lot' && (
            <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item
                name="lotNo"
                label="Lot No"
              >
                <Select
                  placeholder="Select Lot No"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {lotNumDropdownData.map(lot => {
                    return <Option value={lot.lotNumber}>{lot.lotNumber}</Option>
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item
              name="quality"
              label="Quality"
            >
              <Select
                placeholder="Select Quality"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >

                {Object.values(QualityTypeEnum).map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify='end'>
          <Col style={{ textAlign: 'right', paddingTop: '30px' }}>
            <Button type="primary" htmlType="submit" >
              Submit
            </Button>
            <Button htmlType="button" style={{ margin: '0 14px' }} onClick={onReset}>
              Reset
            </Button>
          </Col>
        </Row>
      </Form>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
      >
        <Tabs.TabPane tab="Silk Fiber Lab Analysis" key="work-order">
          <Table
            columns={getFibreLabAnalysisColumns()}
            dataSource={fibreLabAnalysisData}
            loading={loading}
            rowKey="id"
            pagination={{
                    current: page,
                    onChange: (current) => setPage(current),
                  }}
            scroll={{ x: 'max-content' }}
            bordered
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Silk Fiber Analysis" key="lot">
          <Table
            columns={getFibreAnalysisColumns()}
            dataSource={fibreAnalysisData}
            loading={loading}
            rowKey="id"
            pagination={{
                    current: page,
                    onChange: (current) => setPage(current),
                  }}
            scroll={{ x: 'max-content' }}
            bordered
          />
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title={
          <div
            style={{
              background: '#69c0ff',
              padding: '10px 16px',
              margin: '-24px -24px 0 -24px',
              borderRadius: '8px 8px 0 0',
              fontWeight: 600,
              color: '#fff',
            }}
          >
            {analysisType === 'fiber'
              ? 'Edit Silk Fiber Analysis'
              : 'Edit Silk Fiber Lab Analysis'}
          </div>
        }
        visible={isModalVisible}
        onCancel={closeModal}
        bodyStyle={{ padding: '12px 16px' }}
        footer={null}
        width={1200}
      >
        {selectedRecord && (
          <div style={{ marginBottom: 30 }}>
            <Row gutter={8}>
              {selectedRecord.analysisType === 'SILKFIBERANALYSIS' && (
                <>
                
                
                <Col span={6}>
                  
                  <b>Lot Number:</b> {selectedRecord.lotNumber || ''}
                  
                </Col>
                <Col span={6}><b>Operation:</b> {selectedRecord.operation}</Col>
                <Col span={6}><b>Prev Operation:</b> {selectedRecord.prevOperation}</Col>
                </>
              )}
              {selectedRecord.analysisType === 'SILKFIBERLABANALYSIS' && (
                <>
                <Col span={6}>
                  <b>Test Date:</b> {(selectedRecord.testDate)}
                </Col>
                <Col span={6}>
                 <b>Work Order No:</b> {selectedRecord.workOrderNo} 
                </Col>
                
                </>
              )}
              <Col span={6}><b>Variant:</b> {selectedRecord.product}</Col>
              {selectedRecord.analysisType === 'SILKFIBERLABANALYSIS' && (
                <>
              <Col span={6}><b>Quality:</b> {selectedRecord.quality ? selectedRecord.quality : '-'}</Col>
                </>
              )}
            </Row>
            <Row gutter={8} style={{ marginTop: 16 }}>
              <Col span={6}><b>Quantity:</b> {Number(Number(selectedRecord.quantity).toFixed(2))}</Col>
            </Row>
          </div>
        )}

        <Form layout="vertical" form={form} onFinish={onUpdate}>
          <Row gutter={16}>
            {analysisType === 'fiber' && (
              <>
                <Col span={6}>
                  <Form.Item
                    label="Mean Length"
                    name="meanLength"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                        message: 'Only numbers allowed (e.g. 2 or 2.66)',
                      },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    label="Shortest Fiber"
                    name="shortestFiber"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                        message: 'Only numbers allowed (e.g. 2 or 2.66)',
                      },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    label="Longest Fiber"
                    name="longestFiber"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                        message: 'Only numbers allowed (e.g. 2 or 2.66)',
                      },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    label="% of Short Fiber"
                    name="shortFiberPercent"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                        message: 'Only numbers allowed (e.g. 2 or 2.66)',
                      },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    label="Slub"
                    name="slub"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                        message: 'Only numbers allowed (e.g. 2 or 2.66)',
                      },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Remarks" name="remarks">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </>
            )}

            {analysisType === 'lab' && (
              <>
                <Col span={6}>
                  <Form.Item label="Oil Test" name="oilTest" rules={[
                    { required: true, message: 'Required' },
                    {
                      pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                      message: 'Only numbers allowed (e.g. 2 or 2.66)',
                    },
                  ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Oil Test Remarks" name="oilTestRemarks">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Sericin Test" name="sericinTest" rules={[
                    { required: true, message: 'Required' },
                    {
                      pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                      message: 'Only numbers allowed (e.g. 2 or 2.66)',
                    },
                  ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Sericin Test Remarks" name="sericinTestRemarks">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Acidity Test" name="acidityTest" rules={[
                    { required: true, message: 'Required' },
                    {
                      pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                      message: 'Only numbers allowed (e.g. 2 or 2.66)',
                    },
                  ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Acidity Test Remarks" name="acidityTestRemarks">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="PP Content" name="ppContent" rules={[
                    { required: true, message: 'Required' },
                    {
                      pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                      message: 'Only numbers allowed (e.g. 2 or 2.66)',
                    },
                  ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="PP Content Remarks" name="ppContentRemarks">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Moisture Content" name="moistureContent" rules={[
                    { required: true, message: 'Required' },
                    {
                      pattern: /^(?!0+(\.0+)?$)\d+(\.\d+)?$/,
                      message: 'Only numbers allowed (e.g. 2 or 2.66)',
                    },
                  ]}
                  >
                    <InputNumber style={{ width: '100%' }} stringMode />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Moisture Content Remarks" name="moistureContentRemarks">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>

              </>
            )}
          </Row>

          <Row justify="start" style={{ marginTop: 20 }}>
            <Col>
              <Button type="primary" htmlType="submit">
                Update
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        
        visible={printModalVisible}
        onCancel={closePrintModal}
        footer={null}
        width="70%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0, maxHeight: '80vh', overflow: 'auto' }}
      >
        {printRecord && printType === 'fiber' && (
          <QaqcFiberPrint record={printRecord} />
        )}
        {printRecord && printType === 'lab' && (
          <QaqcLabPrint record={printRecord} />
        )}
      </Modal>
    </Card>
  );
};

export default QaqcDetailsView;
